import { cva, type VariantProps } from 'class-variance-authority';
import { Plus } from 'lucide-react';

import { Button } from '../button';
import { cn } from '../../lib/utils';

const BASE_CLASS = 'mm-fab';

const fabVariants = cva(
  `${BASE_CLASS} fixed rounded-full shadow-lg transition-all duration-200 flex items-center justify-center`,
  {
    variants: {
      size: {
        default: 'size-14',
        sm: 'size-12',
        lg: 'size-16',
      },
      position: {
        'bottom-right': 'bottom-6 right-6',
        'bottom-left': 'bottom-6 left-6',
        'top-right': 'top-6 right-6',
        'top-left': 'top-6 left-6',
      },
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-destructive/60',
        outline:
          'border bg-background hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        brand: 'bg-brand text-brand-foreground shadow-xs hover:bg-brand/80',
      },
    },
    defaultVariants: {
      size: 'default',
      position: 'bottom-right',
      variant: 'default',
    },
  }
);

export type FloatingActionButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof fabVariants> & {
    icon?: React.ReactNode;
    onClick?: () => void;
    asChild?: boolean;
  };

const FloatingActionButton = ({
  className,
  size,
  position,
  variant,
  icon,
  ...props
}: FloatingActionButtonProps) => {
  return (
    <Button
      className={cn(fabVariants({ size, position, variant }), className)}
      data-slot="fab"
      {...props}
    >
      {icon || <Plus className="size-6" />}
    </Button>
  );
};
FloatingActionButton.displayName = 'FloatingActionButton';

export { FloatingActionButton, fabVariants };
